





















































































































































import Vue from 'vue';
import { timeDay, timeMonth, timeWeek } from 'd3-time';
import { ascending, groups } from 'd3';
import { ISegment, IState, ISurveyResponseRate } from '@/store/types';
import SessionFilter from '@/components/CancelAnalytics/SessionFilter.vue';
import DatePickerCondensed from '@/components/UI/DatePickerCondensed.vue';
import LineLoader from '@/components/UI/Loader/LineLoader.vue';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import FooterLoader from '@/components/UI/Loader/FooterLoader.vue';
import { mapGetters, mapState } from 'vuex';
import { capitalizeWords, LOAD_STATE } from '@/store/shared';
import * as actions from '@/store/action-types';
import { getDateEndpoints } from '@/helpers/date-utils';
import { formatNumber, formatPercent } from '@/helpers/jsUtils';
import statsService from '@/api/stats.service';
import { SESSION } from '@/helpers/stats-utils';
import LineChartWrapper from '@/components/Vis/D3/Charts/LineChartWrapper.vue';
import PercentChange from '@/components/UI/PercentChange.vue';

interface SurveyData {
  appeared: number;
  selected: number;
  selectedRate: number;
  surveyChoiceOption: string;
  segmentId: string;
  week: string;
  date: string;
}

interface AggregatedData {
  surveyChoiceOption: string; // Adding surveyChoiceOption in the aggregated data to identify it
  totalAppeared: number;
  totalSelected: number;
  weightedAverageRate: number;
  rateDifference?: number;
  rateDifferencePct?: number;
}

interface SuperGroupData {
  startWeek: string;
  endWeek: string;
  totalAppeared: number;
  totalSelected: number;
  weightedAverageRate: number;
}
function aggregateSurveyData(data: SurveyData[]) {
  // 1. Group data by week and surveyChoiceOption
  const groupedData: Record<string, Record<string, SurveyData>> = {};

  data.forEach((item) => {
    groupedData[item.week] = groupedData[item.week] || {};
    groupedData[item.week][item.surveyChoiceOption] = item;
  });

  // 2. Aggregate data for each surveyChoiceOption across all weeks
  const aggregateData: Record<string, AggregatedData> = {};

  Object.keys(groupedData).forEach((week) => {
    Object.keys(groupedData[week]).forEach((option) => {
      const item = groupedData[week][option];
      if (!aggregateData[option]) {
        aggregateData[option] = {
          surveyChoiceOption: option,
          totalAppeared: 0,
          totalSelected: 0,
          weightedAverageRate: 0,
        };
      }

      aggregateData[option].totalAppeared += item.appeared;
      aggregateData[option].totalSelected += item.selected;
    });
  });

  Object.keys(aggregateData).forEach((option) => {
    aggregateData[option].weightedAverageRate = (aggregateData[option].totalSelected / aggregateData[option].totalAppeared) * 100;
  });

  // 3. Calculate the difference in response rate for the last 4 weeks compared to the 4 weeks prior
  const weeks = Object.keys(groupedData).sort(
    (a, b) => new Date(groupedData[a][Object.keys(groupedData[a])[0]].date).getTime() -
      new Date(groupedData[b][Object.keys(groupedData[b])[0]].date).getTime()
  );
  // const last4Weeks = weeks.slice(-4);
  // const previous4Weeks = weeks.slice(-8, -4);

  const calculateAggregateForWeeks = (weeks: string[]): Record<string, AggregatedData> => {
    const aggregate: Record<string, AggregatedData> = {};

    weeks.forEach((week) => {
      for (const option in groupedData[week]) {
        const item = groupedData[week][option];
        if (!aggregate[option]) {
          aggregate[option] = {
            surveyChoiceOption: item.surveyChoiceOption,
            totalAppeared: 0,
            totalSelected: 0,
            weightedAverageRate: 0,
          };
        }

        aggregate[option].totalAppeared += item.appeared;
        aggregate[option].totalSelected += item.selected;
      }
    });

    for (const option in aggregate) {
      aggregate[option].weightedAverageRate = (aggregate[option].totalSelected / aggregate[option].totalAppeared) * 100;
    }

    return aggregate;
  };

  // const aggregateLast4Weeks = calculateAggregateForWeeks(last4Weeks);
  // const aggregatePrevious4Weeks = calculateAggregateForWeeks(previous4Weeks);

  // const rateDifference: Record<string, number> = {};

  // for (const option in aggregateLast4Weeks) {
  //   rateDifference[option] = aggregateLast4Weeks[option].weightedAverageRate - (aggregatePrevious4Weeks[option]?.weightedAverageRate || 0);
  // }

  // // Insert rateDifference and rateDifferencePct into aggregateData
  // for (const option in rateDifference) {
  //   if (aggregateData[option]) {
  //     aggregateData[option].rateDifference = rateDifference[option];
  //     const prevRate = aggregatePrevious4Weeks[option]?.weightedAverageRate || 0;
  //     if (prevRate !== 0) {
  //       aggregateData[option].rateDifferencePct = (rateDifference[option] / prevRate) * 100;
  //     } else {
  //       aggregateData[option].rateDifferencePct = rateDifference[option] > 0 ? 100 : rateDifference[option] < 0 ? -100 : 0;
  //     }
  //   }
  // }

  // 4. Group data into 4-week chunks by surveyChoiceOption
  const superGroups: Record<string, SuperGroupData[]> = {};

  for (let i = 0; i < weeks.length; i += 4) {
    const chunk = weeks.slice(i, i + 4);
    const aggregateChunk = calculateAggregateForWeeks(chunk);

    for (const option in aggregateChunk) {
      if (!superGroups[option]) {
        superGroups[option] = [];
      }

      const totalAppeared = aggregateChunk[option].totalAppeared;
      const totalSelected = aggregateChunk[option].totalSelected;
      const weightedAverageRate = (totalSelected / totalAppeared) * 100;

      superGroups[option].push({
        startWeek: chunk[0],
        endWeek: chunk[chunk.length - 1],
        totalAppeared,
        totalSelected,
        weightedAverageRate,
      });
    }
  }

  // Calculate the rateDifference and rateDifferencePct from the superGroups
  for (const option in superGroups) {
    const superGroupData = superGroups[option];
    if (superGroupData.length >= 2) {
      const last4WeeksRate = superGroupData[superGroupData.length - 1].weightedAverageRate;
      const previous4WeeksRate = superGroupData[superGroupData.length - 2].weightedAverageRate;

      if (aggregateData[option]) {
        aggregateData[option].rateDifference = last4WeeksRate - previous4WeeksRate;
        if (previous4WeeksRate !== 0) {
          aggregateData[option].rateDifferencePct = ((last4WeeksRate - previous4WeeksRate) / previous4WeeksRate) * 100;
        } else {
          aggregateData[option].rateDifferencePct = last4WeeksRate > 0 ? 100 : last4WeeksRate < 0 ? -100 : 0;
        }
      }
    }
  }

  const aggregateArray: AggregatedData[] = Object.entries(aggregateData)
    .map(([option, values]) => ({
      ...values,
    }))
    .sort((a, b) => b.weightedAverageRate - a.weightedAverageRate); // Sorting in descending order of weightedAverageRate

  return {
    aggregateData,
    aggregateArray,
    // rateDifference,
    superGroups,
  };
}

interface SegmentData {
  id: string;
  label: string;
  responses: ISurveyResponseRate[];
  aggregateData: Record<string, AggregatedData>;
  superGroups: Record<string, SuperGroupData[]>;
  aggregateArray: AggregatedData[];
  totalResponses: number;
}

const xField = (d: any) => d.date;
const valueAccessor = (d: any) => d.value;
const color = (d: any) => d.color;

export default Vue.extend({
  name: 'CancellationTrends',
  components: {
    DatePickerCondensed,
    SessionFilter,
    FooterLoader,
    HeaderLoader,
    LineLoader,
    LineChartWrapper,
    PercentChange,
  },
  data() {
    const defaultStartDate = timeDay.offset(new Date(), -180);
    const [startDate, endDate] = getDateEndpoints([defaultStartDate, new Date()]);
    return {
      responseRates: [] as ISurveyResponseRate[],
      loading: true,
      customerFilterSelection: '',
      startDate,
      endDate,
      selectedSegmentId: '' as string | undefined,
      filter: {} as Record<string, string[]>,
      initialLoading: true,
      preselectedFilter: [] as string[],
      formatPercent,
      focusResponse: undefined as string | undefined,
      xField,
      valueAccessor,
      color,
    };
  },
  async created() {
    await this.fetchData();
    this.selectedSegmentId = this.segmentsInPlay[0]?.id;
    this.preselectedFilter = [`${SESSION.SEGMENT_ID}.${this.selectedSegmentId}`];
    this.initialLoading = false;
  },
  computed: {
    ...mapGetters(['lastMonthLabel', 'orgCreatedDate', 'daysSinceOrgCreated', 'waitForData']),
    ...mapState(['surveyResponsesLoadState']),
    dateRange(): [Date, Date] {
      return [this.startDate, this.endDate];
    },
    selectedSegment(): SegmentData | undefined {
      if (!this.selectedSegmentId) {
        return this.totalStats;
      }
      return this.segmentsInPlay.find((d) => d.id === this.selectedSegmentId);
    },
    displayedResponses(): AggregatedData[] {
      if (this.selectedSegment) {
        return this.selectedSegment.aggregateArray.filter((d) => d.totalSelected > 0);
      }
      return [];
    },
    segments(): Record<string, ISegment> {
      const state = this.$store.state as IState;
      return state.segments;
    },
    segmentsInPlay(): SegmentData[] {
      // count the number of responses for each segment
      const segmentCounts = this.responseRates.reduce(
        (acc, d) => {
          if (d.segmentId) {
            acc[d.segmentId] = acc[d.segmentId] ? acc[d.segmentId] + d.selected : d.selected;
          } else {
            acc.null = acc.null ? acc.null + d.selected : d.selected;
          }
          return acc;
        },
        {} as Record<string, number>
      );
      // sort segments by count descending
      const sortedSegmentIds = Object.keys(segmentCounts).sort((a, b) => segmentCounts[b] - segmentCounts[a]);
      // move 'null' to the front of the list if it exists
      if (sortedSegmentIds.includes('null')) {
        sortedSegmentIds.unshift(sortedSegmentIds.splice(sortedSegmentIds.indexOf('null'), 1)[0]);
      }
      // return segment object
      return sortedSegmentIds.map((id) => {
        const responses = this.responseRates.filter((d) => d.segmentId === id || (!d.segmentId && id === 'null'));
        // @ts-ignore
        const { aggregateData, superGroups, aggregateArray } = aggregateSurveyData(responses);
        return {
          id,
          label: this.segments[id]?.name || 'Primary Flow',
          responses,
          aggregateData,
          superGroups,
          aggregateArray,
          totalResponses: segmentCounts[id],
        };
      });
    },
    totalStats(): SegmentData {
      const totalResponses = this.responseRates.reduce((acc, d) => acc + d.selected, 0);
      // @ts-ignore
      const { aggregateData, superGroups, aggregateArray } = aggregateSurveyData(this.responseRates || []);
      return {
        id: '_all',
        label: 'All',
        responses: this.responseRates || [],
        aggregateArray,
        aggregateData,
        superGroups,
        totalResponses,
      };
    },
    lineChartSeries(): any[] {
      if (this.selectedSegment) {
        const seriesKeys = Object.keys(this.selectedSegment.superGroups);
        const series = seriesKeys.map((sk) => {
          // @ts-ignore
          const sg = this.selectedSegment.superGroups[sk] as SuperGroupData[];
          return {
            label: sk,
            id: sk,
            data: sg
              .map((d) => {
                // create date from YYYY-WW (year and week number format)
                const year = +d.startWeek.split('-')[0];
                const weekOfYear = +d.startWeek.split('-')[1];
                const yearStart = new Date(year, 0, 1);
                const date = timeWeek.offset(yearStart, weekOfYear);
                return {
                  date,
                  value: d.weightedAverageRate,
                  label: d.startWeek,
                  ...d,
                };
              })
              .sort((a: any, b: any) => a.date - b.date),
            color: '#435BC8',
          };
        });
        return series.filter((s) => s.data.length > 0);
      }
      return [];
    },
    lineChartHeight(): number {
      const targetHeight = this.displayedResponses.length * 58.8 + 6;
      return Math.min(targetHeight, 400);
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      const mappedFilter = Object.keys(this.filter).reduce(
        (acc, k) => {
          if (this.filter[k].length === 1) {
            acc[k] = this.filter[k][0];
          }
          return acc;
        },
        {} as Record<string, any>
      );
      mappedFilter.startDate = this.startDate;
      mappedFilter.endDate = this.endDate;
      const res = await statsService.surveyResponseRates({ filter: mappedFilter, breakdown: [SESSION.SEGMENT_ID, SESSION.WEEK] });
      this.responseRates = res.map((d) => ({
        ...d,
        // capitalize first letter of each word in survey choice option string
        surveyChoiceOption: capitalizeWords(d.surveyChoiceOption),
      }));
      this.loading = false;
    },
    handleDateChange(value: [Date, Date]): void {
      this.startDate = value[0];
      this.endDate = value[1];
      this.fetchData();
    },
    onFilterUpdate(newFilter: { segmentId?: string[] }) {
      if (newFilter.segmentId && newFilter.segmentId.length) {
        this.selectedSegmentId = newFilter.segmentId[0];
      } else {
        this.selectedSegmentId = undefined;
      }
      this.focusResponse = undefined;
    },
    changeFocusResponse(response?: string): void {
      if (response === this.focusResponse) {
        this.focusResponse = undefined;
      } else {
        this.focusResponse = response;
      }
    },
    exportData() {
      this.$store.dispatch(actions.EXPORT_DATA, {
        // @ts-ignore
        data: this.responseRates,
        name: 'response-rates',
        // fields: ['response', 'count', 'date'],
        csv: true,
      });
    },
  },
});
